import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Home, Users, Car, Calendar, DollarSign, CreditCard, FileText, UserCog, LogOut, Menu, X, QrCode, ClipboardCheck, Receipt } from 'lucide-react';
import { api } from '../lib/api';
import { useState, useEffect } from 'react';

export default function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isCollapsed, setIsCollapsed] = useState(false);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
    
    // Check if sidebar was collapsed in previous session
    const collapsed = localStorage.getItem('sidebarCollapsed') === 'true';
    setIsCollapsed(collapsed);
  }, []);

  const handleLogout = async () => {
    try {
      await api.post('/auth/logout');
      localStorage.removeItem('session_token');
      localStorage.removeItem('user');
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const toggleSidebar = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    localStorage.setItem('sidebarCollapsed', newState.toString());
  };

  const menuItems = [
    { path: '/dashboard', icon: Home, label: 'Dashboard' },
    { path: '/drivers', icon: Users, label: 'Drivers' },
    { path: '/vehicles', icon: Car, label: 'Vehicles' },
    { path: '/assignments', icon: Calendar, label: 'Assignments' },
    { path: '/salary', icon: DollarSign, label: 'Salary' },
    { path: '/attendance', icon: ClipboardCheck, label: 'Attendance' },
    { path: '/payments/generate', icon: CreditCard, label: 'Payment Links' },
    { path: '/payments/reports', icon: FileText, label: 'Payment Reports' },
    { path: '/invoicing', icon: Receipt, label: 'GST Invoicing' },
    { path: '/qr-code', icon: QrCode, label: 'Driver QR Code' },
  ];

  if (user?.role === 'admin') {
    menuItems.push({ path: '/employees', icon: UserCog, label: 'Employees' });
  }

  return (
    <>
      <div 
        className={`${isCollapsed ? 'w-20' : 'w-64'} bg-surface h-screen fixed left-0 top-0 border-r border-border flex flex-col transition-all duration-300 z-50`} 
        data-testid="sidebar"
      >
        {/* Toggle Button */}
        <div className="absolute -right-3 top-6 z-50">
          <button
            onClick={toggleSidebar}
            className="bg-shizah-blue text-white p-1.5 rounded-full shadow-lg hover:bg-blue-700 transition-colors"
            data-testid="toggle-sidebar"
          >
            {isCollapsed ? <Menu size={16} /> : <X size={16} />}
          </button>
        </div>

        {/* Logo */}
        <div className="p-4 border-b border-border flex items-center justify-center">
          {!isCollapsed ? (
            <img
              src="https://customer-assets.emergentagent.com/job_9308f2ee-4a90-4036-bc1e-09ce5941433b/artifacts/cosm4gyf_Shizah.jpeg"
              alt="Shizah Mobility"
              className="w-32 mx-auto"
              data-testid="logo"
            />
          ) : (
            <div className="w-10 h-10 bg-shizah-blue rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-lg">S</span>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100" data-testid="nav-menu">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase()}`}
                title={isCollapsed ? item.label : ''}
                className={`flex items-center ${isCollapsed ? 'justify-center' : 'gap-3'} px-3 py-2 mb-1 rounded-md transition-all duration-200 ${
                  isActive
                    ? 'bg-shizah-blue text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <Icon size={18} />
                {!isCollapsed && <span className="font-medium text-sm">{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        {/* User Info & Logout */}
        <div className="p-3 border-t border-border">
          {user && !isCollapsed && (
            <div className="mb-3" data-testid="user-info">
              <p className="text-sm font-medium text-gray-900 truncate">{user.name}</p>
              <p className="text-xs text-gray-500 truncate">{user.email}</p>
              <p className="text-xs text-shizah-green font-semibold mt-1">{user.role}</p>
            </div>
          )}
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            title={isCollapsed ? 'Logout' : ''}
            className={`flex items-center ${isCollapsed ? 'justify-center' : 'gap-3'} px-4 py-2 w-full rounded-md text-red-600 hover:bg-red-50 transition-all duration-200`}
          >
            <LogOut size={20} />
            {!isCollapsed && <span className="font-medium">Logout</span>}
          </button>
        </div>
      </div>
      
      {/* Main content offset */}
      <div className={`${isCollapsed ? 'ml-20' : 'ml-64'} transition-all duration-300`} />
    </>
  );
}